<template>
  <el-dialog
    :model-value="open"
    @update:model-value="emit('update:open', $event)"
    :title="title"
    width="700px"
    :close-on-click-modal="false"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="120px"
      v-loading="loading"
    >
      <el-form-item label="公告名称" prop="name">
        <el-input v-model="form.name" placeholder="请输入公告名称" />
      </el-form-item>
      <el-form-item label="内容" prop="content">
        <el-input v-model="form.content" placeholder="请输入公告内容" />
      </el-form-item>
      <!-- 修改点：添加 prop="beginTime" 使其成为必选项 -->
      <el-form-item label="活动开始时间" prop="beginTime">
        <el-date-picker
          v-model="form.beginTime"
          type="datetime"
          placeholder="请选择开始时间"
          value-format="YYYY-MM-DD HH:mm:ss"
          format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item label="活动结束时间">
        <el-date-picker
          v-model="form.endTime"
          type="datetime"
          placeholder="请选择结束时间"
          value-format="YYYY-MM-DD HH:mm:ss"
          format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item label="图片">
        <UploadComponent
          ref="uploadComponentRef"
          :on-success="handleImageSuccess"
        >
          <el-button type="primary">上传图片</el-button>
        </UploadComponent>
        <!-- 图片预览 -->
        <div v-if="form.image" class="image-preview">
          <el-image
            style="width: 100px; height: 100px"
            :src="form.image"
            fit="cover"
          >
            <template #error>
              <div class="image-slot">加载失败</div>
            </template>
          </el-image>
          <!-- 添加删除按钮 -->
          <div class="delete-wrapper" @click.stop="removeImage()">
            <el-icon class="delete-icon"><Delete /></el-icon>
          </div>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="cancel">取消</el-button>
        <el-button type="primary" @click="handleConfirm">确定</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ElMessage } from "element-plus";
import { ref, toRef, watch, toRaw } from "vue";
import { addNotice, updateNotice } from "@/notice/notice";
import UploadComponent from "@/components/UploadComponent.vue";
import type { FormInstance } from "element-plus";

// 确保 emit 包含 'update:open' 事件
const emit = defineEmits(["update:open", "refreshList"]);

// 定义 props
const props = defineProps({
  title: {
    type: String,
    default: "新增",
  },
  open: {
    type: Boolean,
    default: false,
  },
  editData: {
    // 添加编辑数据的 prop
    type: Object,
    default: () => null,
  },
});

// 表单数据
const formRef = ref();

// 表单验证规则 - beginTime 已包含在规则中
const rules = {
  name: [{ required: true, message: "请输入公告名称", trigger: "blur" }],
  content: [{ required: true, message: "请输入公告内容", trigger: "blur" }],
  beginTime: [
    { required: true, message: "请选择活动开始时间", trigger: "change" },
  ],
};
const loading = ref(false);
const handleImageSuccess = (imageUrl: string) => {
  if (!imageUrl) {
    console.log("收到空的图片URL");
    return;
  }

  form.value.image = imageUrl;
  ElMessage.success("图片上传成功");
};
// 修改 form 的定义和初始化
const form = ref({
  id: "",
  name: "",
  content: "",
  beginTime: "",
  endTime: "",
  image: "",
});
// 确保在所有需要格式化日期的地方使用相同的函数
const formatDateToString = (date: string | Date | null) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(
    2,
    "0"
  )}:${String(d.getMinutes()).padStart(2, "0")}:${String(
    d.getSeconds()
  ).padStart(2, "0")}`;
};
// 添加 watch 来监听 editData 的变化
watch(
  () => props.editData,
  (newVal) => {
    if (newVal) {
      // 转换日期字段为字符串
      const formattedData = {
        ...newVal,
        beginTime: formatDateToString(newVal.beginTime),
        endTime: formatDateToString(newVal.endTime),
      };
      Object.assign(form.value, formattedData);
    } else {
      // 当没有编辑数据时，重置表单
      form.value = {
        id: "",
        name: "",
        content: "",
        beginTime: "",
        endTime: "",
        image: "",
      };
    }
  },
  { immediate: true }
);

/** 提交表单 */
const handleConfirm = async () => {
  if (!formRef.value) return;

  await formRef.value.validate(async (valid: boolean) => {
    if (!valid) {
      ElMessage.warning("请填写必填项");
      return;
    }

    try {
      loading.value = true;

      const noticeData = {
        id: form.value.id, // 添加id字段
        name: form.value.name,
        beginTime: form.value.beginTime,
        endTime: form.value.endTime,
        content: form.value.content,
        image: form.value.image,
      };

      // 根据是否存在id判断是新增还是编辑
      const response = noticeData.id
        ? await updateNotice(noticeData, noticeData.id)
        : await addNotice(noticeData);

      if (response?.code === 1) {
        ElMessage.success(noticeData.id ? "修改成功" : "新增成功");
        emit("refreshList");
        emit("update:open", false);
        resetForm(formRef.value);
      } else {
        ElMessage.error(response?.msg || "操作失败");
      }
    } catch (error) {
      console.error("操作失败:", error);
      ElMessage.error("操作失败");
    } finally {
      loading.value = false;
    }
  });
};
// 重置表单
const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formEl.resetFields();
};

// 取消操作
const cancel = () => {
  // 重置表单
  resetForm(formRef.value);
  // 触发父组件关闭对话框的事件
  emit("update:open", false);
};
// 删除图片
const removeImage = () => {
  form.value.image = "";
};
</script>
<style scoped>
/* 在原有样式基础上添加以下规则 */
.image-preview {
  position: relative;
  display: inline-block;
}

.delete-wrapper {
  position: absolute;
  top: 4px;
  right: 4px;
  display: none;
  cursor: pointer;
  z-index: 2;
}

.image-preview:hover .delete-wrapper {
  display: flex;
}

.delete-icon {
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  padding: 4px;
  font-size: 16px;
  transition: all 0.3s;
}

.delete-icon:hover {
  background: rgba(255, 0, 0, 0.8);
}
</style>
